import { useId } from 'react';
import { InfoIcon, WarningCircleIcon } from '@phosphor-icons/react';
import './checkout_price_summary.css';

export interface CheckoutPriceLine {
  id: string;
  label: string;
  /** Ausente ⇒ a linha mostra só o rótulo (ex.: 0 diárias). */
  value?: string;
}

export interface CheckoutPriceSummaryCopy {
  submitting: string;
  back: string;
  retry: string;
  approvalNote: string;
}

export interface CheckoutPriceSummaryProps {
  /** Linhas antes do total, na ordem: itens do carrinho, diárias, valor por pessoa. */
  lines: CheckoutPriceLine[];
  total: { label: string; value: string };
  /** "Solicitar voo · R$ 1.182". */
  submitLabel: string;
  submitting: boolean;
  canSubmit: boolean;
  /** O que ainda trava o CTA, já em frase ("Para solicitar, preencha: …"). Ligado ao
   *  botão por `aria-describedby`; sem isto o botão ficava cinza sem motivo (#4206). */
  blockers?: string;
  /** Faixa âmbar do erro determinado; o retry é o próprio envio. */
  notice?: string;
  /** false ⇒ a faixa sai sem o "Tentar de novo": reenviar o mesmo form recusaria
   *  igual (#6566). O CTA principal segue para depois da correção. */
  noticeRetry?: boolean;
  onSubmit: () => void;
  /** Saída secundária do rodapé — o MESMO fechamento do ✕. Existe porque no touch o
   *  ✕ mora longe do polegar; o CSS a esconde a partir da régua `wide`. */
  onBack?: () => void;
  copy: CheckoutPriceSummaryCopy;
}

/** Linhas de preço, CTA valorado, pendências e nota de aprovação: o rodapé do cartão
 *  "Sua viagem" (`CheckoutTripSummary`). */
export function CheckoutPriceSummary({
  lines,
  total,
  submitLabel,
  submitting,
  canSubmit,
  blockers,
  notice,
  noticeRetry = true,
  onSubmit,
  onBack,
  copy,
}: Readonly<CheckoutPriceSummaryProps>) {
  const blockersId = useId();
  // As pendências somem enquanto envia: o CTA já diz "Solicitando".
  const blockersText = submitting ? undefined : blockers;
  return (
    <div className="cpc">
      {lines.map((line) => (
        <div className="cpc-row" key={line.id}>
          <span className="cpc-row-label">{line.label}</span>
          {line.value !== undefined && <span className="cpc-row-value">{line.value}</span>}
        </div>
      ))}
      <div className="cpc-divider" />
      <div className="cpc-row cpc-row--total">
        <span className="cpc-row-label">{total.label}</span>
        <span className="cpc-row-value">{total.value}</span>
      </div>
      {/* As ações juntas: no celular o CSS as leva para o fim do cartão, abaixo dos
          avisos que explicam o estado do CTA (ordem só visual; o foco segue a do DOM). */}
      <div className="cpc-actions">
        <button
          type="button"
          className="cpc-submit"
          onClick={onSubmit}
          disabled={!canSubmit || submitting}
          aria-busy={submitting}
          aria-describedby={blockersText && blockersId}
        >
          {submitting ? (
            <>
              <span className="cpc-spin" aria-hidden="true" />
              {copy.submitting}
            </>
          ) : (
            submitLabel
          )}
        </button>
        {onBack && (
          <button type="button" className="cpc-back" onClick={onBack} disabled={submitting}>
            {copy.back}
          </button>
        )}
      </div>
      {/* Live region SEMPRE montada (vazia e `sr-only`, fora do gap do flex):
          inserida já com texto, leitores de tela pulam o anúncio. */}
      <p
        id={blockersId}
        role="status"
        className={
          blockersText
            ? 'cpc-blockers m-0 flex items-start gap-2 rounded-md bg-warning-soft px-3 py-2 text-detail font-medium text-warning-strong'
            : 'sr-only'
        }
      >
        {blockersText && (
          <>
            <WarningCircleIcon size={16} className="mt-0.5 shrink-0" aria-hidden />
            <span>{blockersText}</span>
          </>
        )}
      </p>
      {notice && (
        <div className="cpc-notice" role="alert">
          <p className="cpc-notice-text">{notice}</p>
          {noticeRetry && (
            <button
              type="button"
              className="cpc-notice-retry"
              onClick={onSubmit}
              disabled={!canSubmit || submitting}
            >
              {copy.retry}
            </button>
          )}
        </div>
      )}
      <p className="cpc-note">
        <span className="cpc-note-icon" aria-hidden="true">
          <InfoIcon size={16} aria-hidden />
        </span>
        <span>{copy.approvalNote}</span>
      </p>
    </div>
  );
}
